import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Button, InputField } from '@salt/components';
import moment from 'moment';
import BackArrow from '../../media/icons/backarrow_black.svg';
import { getProject, updateProject, getProjects } from '../../api/users';

import MenuIcon from '../../media/icons/menu.svg';
import Switch from '../Switch';
import { getCategories } from '../../api/reports';
import EditProjectModal from './EditProjectModal';
import DeleteProjectModal from './DeleteProjectModal';
import EditProjectMembersModal from './EditProjectMembersModal';
import RemoveUserModal from './RemoveUserModal';
import MoveProjectModal from './MoveProjectModal';
import EditCategoriesModal from './EditCategoriesModal';

const ProjectDetail = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const { id } = useParams();

  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editMembersModal, setEditMembersModal] = useState(false);
  const [removeUserModal, setRemoveUserModal] = useState(false);
  const [moveUserModal, setMoveUserModal] = useState(false);
  const [editCategoriesModal, setEditCategoriesModal] = useState(false);

  const [project, setProject] = useState(undefined);
  const [projects, setProjects] = useState(undefined);
  const [categories, setCategories] = useState(undefined);

  const [userMenu, setUserMenu] = useState(undefined);
  const [memberSearch, setMemberSearch] = useState('');

  const fetchData = useCallback(async () => {
    // setLoading(true);
    try {
      const projectData = await getProject({
        id,
      });
      const categoryData = await getCategories();
      const { projectItems } = await getProjects({ limit: 1000 });

      setProject(projectData);
      setProjects(projectItems);
      setCategories(categoryData);
    } catch (error) {
      console.error(error.message);
    }
    // setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the ref exists and the click target is NOT inside the menu ref or menu button ref
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setUserMenu(undefined);
      }
    };

    // Only add listener if the element is open
    if (userMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenu]);

  const handleMenuOpen = useCallback(
    (userId) => {
      if (userMenu === userId) {
        setUserMenu(undefined);
      } else {
        setUserMenu(userId);
      }
    },
    [userMenu]
  );

  const filteredUsers = useMemo(
    () =>
      project?.members?.filter((m) => m.name?.toLowerCase().includes(memberSearch.toLowerCase())) ||
      [],
    [project?.members, memberSearch]
  );

  const handleCapabilityChange = useCallback(
    (key) => {
      if (key === 'smart') {
        updateProject({ projectId: project.id, smartEnabled: !project.smart_enabled }).then(() =>
          fetchData()
        );
      } else if (key === 'situational_awareness') {
        updateProject({
          projectId: project.id,
          situationalAwarenessEnabled: !project.situational_awareness_enabled,
        }).then(() => fetchData());
      }
    },
    [project?.situational_awareness_enabled, project?.smart_enabled]
  );

  return (
    <main
      style={{
        width: '100%',
        padding: '25px 50px',
      }}
    >
      <div className="flex">
        <button type="button" onClick={() => navigate(-1)}>
          <img src={BackArrow} alt="back" />
        </button>
        <span className="text-[24px] h-full my-auto mx-4 font-medium">Project details</span>
      </div>
      <div className="mt-8 flex mx-2 border-b border-solid border-[#B4B9C1] pb-4">
        <div>
          <div className="block">
            <div
              style={{ backgroundColor: project?.accent_colour }}
              className="w-4.5 h-4.5 rounded-full inline-flex m-auto"
            />
            <span className="text-[22px] mx-4 font-medium">Project: {project?.name}</span>
          </div>
          <span className="text-lg block my-2 font-medium">
            Created: {moment(project?.created_at).format('MMM Do, YYYY, HH:mm')}
          </span>
        </div>

        <div className="ml-auto mt-auto mb-2">
          <Button
            type="positive"
            onClick={() => setEditModal(true)}
            paddingClassName="py-2 px-4"
            className="mx-2"
          >
            Edit
          </Button>
          <Button
            type="negative"
            onClick={() => setDeleteModal(true)}
            paddingClassName="py-2 px-4"
            className="mx-2"
          >
            Delete
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-10 mt-6 mb-20">
        <div className="p-2">
          <div className="flex">
            <span className="text-lg">Members</span>
            <Button
              type="positive"
              onClick={() => setEditMembersModal(true)}
              paddingClassName="py-2 px-4"
              className="ml-auto grow-0"
            >
              Edit Members
            </Button>
          </div>
          <div className="my-4">
            <InputField
              icon="search"
              iconPlacement="left"
              onChange={(event) => setMemberSearch(event.target.value)}
              onClear={() => setMemberSearch('')}
              placeholder="Search Members"
              value={memberSearch}
              labelBackgroundClass="peer-focus:bg-secondary peer-[:not(:placeholder-shown)]:bg-secondary"
              backgroundClass="bg-secondary-light"
            />
          </div>
          {filteredUsers.map((m) => (
            <div key={m.id}>
              <div className="flex items-center p-4 bg-secondary-light border-b border-solid border-[#E7E7E7] text-base/normal">
                {m.name}
                <div className="ml-auto relative w-5 h-5">
                  <button
                    ref={userMenu === m.id ? buttonRef : undefined}
                    onClick={() => handleMenuOpen(m.id)}
                    className="my-auto"
                    type="button"
                  >
                    <img style={{ width: '100%', height: '100%' }} src={MenuIcon} alt="" />
                  </button>
                  {userMenu === m.id && (
                    <div ref={containerRef} className="absolute z-10 -right-12 flex text-nowrap">
                      <div className="bg-white shadow-xl/10">
                        <button
                          type="button"
                          onClick={() => setMoveUserModal(m)}
                          className="block px-4 py-2 text-base font-normal text-gray-700 w-full text-left
                                              hover:bg-gray-100"
                        >
                          Move Projects
                        </button>
                        <button
                          type="button"
                          onClick={() => setRemoveUserModal(m)}
                          className="block px-4 py-2 text-base font-normal w-full text-left
                                              hover:bg-gray-100 text-negative"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-2">
          <div className="mb-8">
            <div className="mb-6">
              <span className="text-lg">Capabilities</span>
            </div>
            <div className="flex items-center p-4 bg-secondary-light border-b border-solid border-[#E7E7E7]">
              <div>
                <div className="text-lg/normal block">Situational Awareness</div>
                <div className="text-base block text-[#434B58]/60">
                  Users can share their location to the C&C centre.
                </div>
              </div>
              <Switch
                sizeClass="w-9 h-5 after:h-6 after:w-6"
                className="ml-auto mr-2"
                value={project?.situational_awareness_enabled}
                onClick={() => handleCapabilityChange('situational_awareness')}
              />
            </div>
            <div className="flex items-center p-4 bg-secondary-light border-b border-solid border-[#E7E7E7]">
              <div>
                <div className="text-lg/normal block">
                  SMART (Secure Messaging and Reporting Tool)
                </div>
                <div className="text-base block text-[#434B58]/60">
                  Users can submit report categories which can be viewed in the C&C centre.
                </div>
              </div>
              <Switch
                sizeClass="w-9 h-5 after:h-6 after:w-6"
                className="ml-auto mr-2"
                value={project?.smart_enabled}
                onClick={() => handleCapabilityChange('smart')}
              />
            </div>
          </div>

          <div className="flex mb-4">
            <span className="text-lg">Categories</span>
            <Button
              type="positive"
              onClick={() => setEditCategoriesModal(true)}
              paddingClassName="py-2 px-4"
              className="ml-auto grow-0"
            >
              Edit Categories
            </Button>
          </div>

          {project?.categories.map((c) => {
            const category = categories.find((cat) => cat.id === c);
            if (!category) return null;
            return (
              <div
                key={c}
                className="flex items-center p-4 bg-secondary-light border-b border-solid border-[#E7E7E7] text-base/normal"
              >
                <img
                  className="w-10 h-10"
                  src={`data:image/svg+xml;base64,${category.icon}`}
                  alt={`${category.id}-icon`}
                />
                <span className="mx-4">{category.name}</span>
              </div>
            );
          })}
        </div>
      </div>
      {editModal && (
        <EditProjectModal
          handleEditProjectModalClose={() => setEditModal(false)}
          fetchData={fetchData}
          project={project}
        />
      )}
      {removeUserModal && (
        <RemoveUserModal
          handleRemoveUserModalClose={() => setRemoveUserModal(undefined)}
          fetchData={fetchData}
          user={removeUserModal}
          project={project}
        />
      )}
      {moveUserModal && (
        <MoveProjectModal
          user={{ ...moveUserModal, project }}
          projects={projects.filter((p) => p.id !== project.id)}
          handleMoveProjectModalClose={() => setMoveUserModal(false)}
          fetchData={fetchData}
        />
      )}
      {deleteModal && (
        <DeleteProjectModal
          handleDeleteProjectModalClose={() => setDeleteModal(false)}
          project={project}
        />
      )}
      {editMembersModal && (
        <EditProjectMembersModal
          handleEditMembersModalClose={() => setEditMembersModal(false)}
          project={project}
          fetchData={fetchData}
        />
      )}
      {editCategoriesModal && (
        <EditCategoriesModal
          handleEditCategoriesModalClose={() => setEditCategoriesModal(false)}
          project={project}
          fetchData={fetchData}
        />
      )}
    </main>
  );
};

export default ProjectDetail;
